<script setup lang="ts">
import { VueFinalModal } from 'vue-final-modal'
import IconClose from '@boats/ui/components/icons/IconClose.vue'
import { useSiteLanguages } from '~/composables/useSiteLanguages'

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
}>()
const { locale, t } = useI18n()
const currency = useCurrencyStore()
const { languages } = useSiteLanguages()
const currentLanguage = computed(() =>
  languages.value.find((language) => language.code === locale.value),
)
const hasCurrencies = computed(() => (currency.list?.length ?? 0) > 1)
const title = computed(() =>
  hasCurrencies.value
    ? `${t('string.Language')} & ${t('string.Currency')}`
    : t('string.Language'),
)
const activeTab = ref<'currency' | 'language'>('currency')
const currencyLabel = (option: typeof currency.selected | undefined) =>
  [option?.code, option?.sign].filter(Boolean).join(' ')
const currencyFlags: Record<string, string> = {
  GBP: 'en',
  DKK: 'da',
  NOK: 'nn',
  SEK: 'se',
}

const close = () => emit('update:modelValue', false)
// BOAT-2013: opening the sheet counts as "seen the selector".
const { visible: currencyToastVisible, dismiss: dismissCurrencyToast } =
  useCurrencyToast()
watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    dismissCurrencyToast()
    activeTab.value = hasCurrencies.value ? 'currency' : 'language'
  },
  { immediate: true },
)
const selectCurrency = async (code: string) => {
  if (currency.isSelecting) return
  if (code !== currency.selected?.code) {
    await currency.select(code)
    // A gift-card cart or failed cart lookup leaves the sheet open, with
    // the existing store showing the reason and keeping the currency intact.
    if (currency.selected?.code !== code) return
  }
  close()
}
const switchTab = (event: KeyboardEvent, tab: 'currency' | 'language') => {
  activeTab.value = tab
  const button = event.currentTarget as HTMLElement
  const sibling =
    tab === 'language'
      ? button.nextElementSibling
      : button.previousElementSibling
  ;(sibling as HTMLElement | null)?.focus()
}

let desktopQuery: MediaQueryList | undefined
const closeOnDesktop = () => {
  if (desktopQuery?.matches) close()
}
onMounted(() => {
  desktopQuery = window.matchMedia('(min-width: 1200px)')
  desktopQuery.addEventListener('change', closeOnDesktop)
})
onBeforeUnmount(() =>
  desktopQuery?.removeEventListener('change', closeOnDesktop),
)
</script>

<template>
  <div class="app-region-mobile xl:!hidden">
    <button
      type="button"
      class="app-region-mobile__strip"
      :aria-label="title"
      :aria-expanded="modelValue"
      aria-haspopup="dialog"
      @click="emit('update:modelValue', !modelValue)"
    >
      <img :src="`/images/flags/${locale}.png`" alt="" />
      <span>{{ currentLanguage?.name }}</span>
      <template v-if="hasCurrencies">
        <span class="app-region-mobile__divider" aria-hidden="true"></span>
        <span class="app-region-mobile__currency" dir="ltr">
          {{ currencyLabel(currency.selected) }}
        </span>
      </template>
      <span v-if="modelValue" class="app-region-mobile__close-label">
        {{ $t('string.Close') }}
      </span>
      <IconChevronDown
        class="app-region-mobile__chevron"
        :class="{ 'is-open': modelValue }"
        aria-hidden="true"
      />
    </button>

    <AppCurrencyToast
      v-if="hasCurrencies && currencyToastVisible"
      arrow="left"
      @dismiss="dismissCurrencyToast"
    />
  </div>

  <VueFinalModal
    :model-value="modelValue"
    class="app-region-mobile__modal"
    content-class="app-region-mobile__sheet"
    overlay-class="app-region-mobile__overlay"
    overlay-transition="vfm-fade"
    content-transition="vfm-slide-down"
    role="dialog"
    aria-modal="true"
    :aria-label="title"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="app-region-mobile__handle" aria-hidden="true"></div>
    <header class="app-region-mobile__header">
      <h2>{{ title }}</h2>
      <button type="button" :aria-label="$t('string.Close')" @click="close">
        <IconClose aria-hidden="true" />
      </button>
    </header>

    <div
      v-if="hasCurrencies"
      class="app-region-mobile__tabs"
      role="tablist"
      :aria-label="title"
    >
      <button
        id="mobile-region-currency-tab"
        type="button"
        role="tab"
        :aria-selected="activeTab === 'currency'"
        aria-controls="mobile-region-currency-panel"
        :tabindex="activeTab === 'currency' ? 0 : -1"
        @click="activeTab = 'currency'"
        @keydown.right.prevent="switchTab($event, 'language')"
        @keydown.left.prevent="switchTab($event, 'language')"
      >
        <span>{{ $t('string.Currency') }}</span>
        <strong dir="ltr">{{ currencyLabel(currency.selected) }}</strong>
      </button>
      <button
        id="mobile-region-language-tab"
        type="button"
        role="tab"
        :aria-selected="activeTab === 'language'"
        aria-controls="mobile-region-language-panel"
        :tabindex="activeTab === 'language' ? 0 : -1"
        @click="activeTab = 'language'"
        @keydown.right.prevent="switchTab($event, 'currency')"
        @keydown.left.prevent="switchTab($event, 'currency')"
      >
        <span>{{ $t('string.Language') }}</span>
        <strong>{{ currentLanguage?.name }}</strong>
      </button>
    </div>

    <div
      v-if="hasCurrencies && activeTab === 'currency'"
      id="mobile-region-currency-panel"
      class="app-region-mobile__rows"
      role="tabpanel"
      aria-labelledby="mobile-region-currency-tab"
    >
      <button
        v-for="option in currency.list"
        :key="option.code"
        type="button"
        class="app-region-mobile__row"
        :aria-pressed="currency.selected?.code === option.code"
        :disabled="currency.isSelecting"
        @click="selectCurrency(option.code)"
      >
        <span class="app-region-mobile__mark" aria-hidden="true">
          <img
            v-if="currencyFlags[option.code]"
            :src="`/images/flags/${currencyFlags[option.code]}.png`"
            alt=""
          />
          <span v-else>{{ option.sign }}</span>
        </span>
        <strong>{{ option.code }}</strong>
        <span class="app-region-mobile__name">{{ option.name }}</span>
        <IconCheck
          v-if="currency.selected?.code === option.code"
          aria-hidden="true"
        />
      </button>
    </div>
    <div
      v-else
      id="mobile-region-language-panel"
      class="app-region-mobile__rows"
      :role="hasCurrencies ? 'tabpanel' : undefined"
      :aria-labelledby="
        hasCurrencies ? 'mobile-region-language-tab' : undefined
      "
    >
      <a
        v-for="language in languages"
        :key="language.code"
        :href="language.link"
        class="app-region-mobile__row"
        :aria-current="language.code === locale ? 'true' : undefined"
      >
        <span class="app-region-mobile__mark" aria-hidden="true">
          <img :src="`/images/flags/${language.code}.png`" alt="" />
        </span>
        <strong class="app-region-mobile__name">{{ language.name }}</strong>
        <IconCheck v-if="language.code === locale" aria-hidden="true" />
      </a>
    </div>
  </VueFinalModal>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-region-mobile {
  position: relative;

  .app-currency-toast {
    top: calc(100% + 0.625rem);
    left: 1rem;
    right: 1rem;
    white-space: normal;

    // Point at the currency label ("USD $") rather than the language flag.
    &::before {
      left: 7.75rem;
    }
  }
}
.app-region-mobile__strip {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 44px;
  padding: 0 16px;
  border-top: 1px solid #2b2d35;
  background: #2a2c34;
  color: $white;
  font-size: 14px;

  img {
    width: 22px;
    height: 22px;
    border-radius: 50%;
  }
}
.app-region-mobile__divider {
  width: 1px;
  height: 16px;
  background: #5a5e69;
}
.app-region-mobile__currency {
  font-family: $font-barlow;
  font-weight: 600;
  letter-spacing: 0.06em;
}
.app-region-mobile__close-label {
  margin-left: auto;
  font-family: $font-barlow;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}
.app-region-mobile__chevron {
  width: 14px;
  height: 14px;
  margin-left: auto;
  &.is-open {
    margin-left: 0;
    transform: rotate(180deg);
  }
}
.app-region-mobile__modal {
  top: var(--navbar-offset-height, 100px);
  display: flex;
  align-items: flex-end;
  overscroll-behavior: none;
}
.app-region-mobile__overlay {
  background: rgba(8, 9, 12, 0.62);
}
.app-region-mobile__sheet {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 676px;
  max-height: calc(100dvh - var(--navbar-offset-height, 100px) - 16px);
  overflow: hidden;
  border-radius: 18px 18px 0 0;
  background: $white;
  color: $black;
  box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.25);
  touch-action: auto;

  button,
  a {
    &:focus-visible {
      outline: 2px solid $red;
      outline-offset: -2px;
    }
  }
}
.app-region-mobile__handle {
  flex-shrink: 0;
  width: 40px;
  height: 4px;
  margin: 8px auto 0;
  border-radius: 2px;
  background: #d3d5da;
}
.app-region-mobile__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 8px 0 20px;
  h2 {
    margin: 0;
    font-family: $font-barlow-condensed;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
  button {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 44px;
    height: 44px;
  }
  svg {
    width: 20px;
    height: 20px;
  }
}
.app-region-mobile__tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  margin: 12px 20px 8px;
  padding: 4px;
  border-radius: 12px;
  background: #f0f1f3;
  button {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    min-height: 52px;
    padding: 0 14px;
    border-radius: 9px;
    text-align: left;
  }
  [aria-selected='true'] {
    background: $white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  }
  span {
    font-family: $font-barlow;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 0.14em;
    color: #5b5f68;
    text-transform: uppercase;
  }
  strong {
    font-weight: 500;
    font-size: 15px;
  }
}
.app-region-mobile__rows {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  padding: 4px 0 max(8px, env(safe-area-inset-bottom));
}
.app-region-mobile__row {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 56px;
  padding: 8px 20px;
  color: $black;
  font-size: 15px;
  text-align: left;
  &[aria-pressed='true'],
  &[aria-current='true'] {
    background: #f3f4f6;
  }
  &:hover {
    background: #f3f4f6;
    text-decoration: none;
    color: $black;
  }
  strong {
    min-width: 44px;
    font-weight: 600;
  }
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: $red;
  }
}
.app-region-mobile__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  background: #ecedf0;
  font-weight: 600;
  font-size: 11px;
  img {
    width: 100%;
    height: 100%;
  }
}
.app-region-mobile__name {
  flex: 1;
  color: #4a4e57;
}
</style>
